/**
 * What the renderer needs to draw a player (read-only). The C64 draws the sprite frame D_8520 (kept
 * as `motion.frame`); this names it.
 */

import type { Facing, Player } from '../types';

export type PlayerPoseName = 'hidden' | 'stand' | 'walk' | 'jump' | 'fall' | 'blow' | 'dying';

export interface PlayerPose {
  pose: PlayerPoseName;
  facing: Facing;
  /**
   * The C64 sprite frame: 0-3 right (2/3 = jump), 4-7 left (6/7 = jump), 8/9 blowing right, $A/$B
   * blowing left, $C-$E (and 0) spinning while dying, $F-$12 lying dead.
   */
  frame: number;
  /** The frame's animation step (bit 0): the C64's two-frame walk / idle / air cycle. */
  step: 0 | 1;
  /** While blowing, 0..5 through the animation (the bubble appears at 2); otherwise -1. */
  blowFrame: number;
  /** While dying, I-ticks into the 88-tick death animation (the body spins for the first 32); otherwise -1. */
  deathTick: number;
  /**
   * Respawn invulnerability: drawn in the flash colour this tick. The C64 XORs the sprite colour with 5
   * every I-tick (D_8548): P1 green <-> black (so it blinks out), P2 cyan <-> blue.
   */
  flash: boolean;
}

export function playerPose(p: Player): PlayerPose {
  const m = p.motion;
  const base = {
    facing: p.facing, frame: m.frame, step: (m.frame & 1) as 0 | 1, blowFrame: -1, deathTick: -1, flash: m.flash,
  };
  if (p.state === 'out' || p.state === 'gameOver') return { ...base, pose: 'hidden' };
  if (p.state === 'dying') {
    const t = m.dead ? 88 : m.deathStarted ? (0x20 - m.anim + 0x100) % 0x100 : 0;
    return { ...base, pose: 'dying', deathTick: t };
  }
  if (m.frame >= 8 && m.frame < 0x0c && m.blow >= 0 && m.blow <= 5) {
    return { ...base, pose: 'blow', blowFrame: 5 - m.blow };
  }
  if (m.jump > 0) return { ...base, pose: 'jump' };
  if (m.jump === 0 || m.falling) return { ...base, pose: 'fall' };
  return { ...base, pose: m.walked ? 'walk' : 'stand' };
}
